import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ProductTechnicalFeaturesAddComponent } from '../product-technical-features-add/product-technical-features-add.component';
import { Product, ProductTechnicalFeatures, TechnicalFeature, ValueTypes } from 'src/app/models';
import { GenericResource } from 'src/app/resources';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { MasterApiService } from 'src/app/services/master-api.service';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxTagBoxComponent } from 'devextreme-angular';
import { FocusInEvent } from 'devextreme/ui/select_box';
import { ColumnEditCellTemplateData } from 'devextreme/ui/data_grid';
import dxTagBox from 'devextreme/ui/tag_box';

@Component({
    selector: 'app-product-features',
    templateUrl: './product-features.component.html',
    styleUrl: './product-features.component.sass',
    standalone: false
})
export class ProductFeaturesComponent implements OnInit, OnDestroy {

  @Input() product?: Product;
  @ViewChild('tagBox', { static: false }) tagBox?: DxTagBoxComponent;
  @ViewChild('features', { static: false }) features?: DxDataGridComponent;
  productTechicalFeatures?: GenericResource<ProductTechnicalFeatures>;
  technicalfeaturesValues: { value: string, display: string }[] = [];
  technicalFeatures: TechnicalFeature[] = [];
  onInitTagBoxValue: string[] = [];
  instance?: dxTagBox;
  destroy$: Subject<void>;
  

  technicalFeatureAdd = ProductTechnicalFeaturesAddComponent;
  valueTypes = ValueTypes.map(x => ({ id: x.id, name: this.translationService.instant(`enumeration.ValueTypes.${x.name}`) }));

  constructor(
    private api: MasterApiService,
    private translationService: TranslateService,
  ) { 
    this.destroy$ = new Subject<void>();
  }
  async ngOnInit(): Promise<void> {
    this.productTechicalFeatures = this.api.resources.product.productTechnicalFeatures(this.product!.id);
    const technicalFeatures = await firstValueFrom(this.api.resources.product.technicalfeatures.getAll());
    this.technicalFeatures = technicalFeatures.map(tf => ({
      ...tf,
      valueTypeName: this.valueTypes.find(v => v.id === tf.valueTypeId)?.name || ''
  }));
  }
  async onFeaturesDataSourceChange($event: any) {
    this.technicalFeatures = await firstValueFrom(this.api.resources.product.technicalfeatures.getAll());
    this.technicalFeatures = this.technicalFeatures.map(tf => {
      const valueType = this.valueTypes.find(v => v.id === tf.valueTypeId);
      return { ...tf, valueTypeName: valueType ? valueType.name : '' };
    });
    this.features?.instance.repaint();
  
  }
  
    getTechnicalfeaturesValues(row : FocusInEvent & { data: ProductTechnicalFeatures }) {
      const id = row.data.technicalFeatureId;
      const values = this.technicalFeatures.find(tf => tf.id === id)!.values;
      if (!values) {
        this.technicalfeaturesValues = [];
      }
      this.technicalfeaturesValues = values.map(value => ({ value, display: value }));
    }
  
    technicalFeaturesRowIsValueType(type:number, row:any) {
      const id = row.data.technicalFeatureId;
      const valueTypeId =this.technicalFeatures.find(tf => tf.id === id)?.valueTypeId;
      return (valueTypeId === type);
    }
  
    technicalFeaturesRowSetValue(type: number, row: any) {
      if (!row.value) {
          row.setValue('');
          return;
      }
  
      let value = row.value;
  
      if (type === 2) {
          this.validateDecimalNumber(value, row ,this.features!);
      }
  
      if (type === 1) {
          if (isNaN(value)) {
              value = '0';
          } else {
              value = Math.round(value).toString();
          }
      }
  
      row.setValue(value.toString());
  }
  
  
    tagBoxValues(row: ColumnEditCellTemplateData<ProductTechnicalFeatures>) {
      this.onInitTagBoxValue = row.value.split(',');
      const id = row.data!.technicalFeatureId;
      const values = this.technicalFeatures.find(tf => tf.id === id)!.values;
      if(!this.onInitTagBoxValue || this.onInitTagBoxValue[0]=== '') {
        this.onInitTagBoxValue = [];
      }
      if (!values) {
        this.technicalfeaturesValues = [];
      }
      this.technicalfeaturesValues = values.map(value => ({ value, display: value }));
  
      this.tagBox!.onValueChanged.pipe(takeUntil(this.destroy$)).subscribe(async e => {
        if (e.value !== e.previousValue) {
          if (e.value !== undefined || this.technicalfeaturesValues.includes(e.value)) {
            row.setValue(e.value.toString());
          }
        }
      })
    }

      validateDecimalNumber(value: string, row: any, features: DxDataGridComponent) {
        // Ensure the value contains only numbers, commas, or points
        const validCharacters = /^[0-9.,]+$/;
    
        // Replace all occurrences of '.' with ','
        value = value.replace(/\./g, ',');
    
        // If there are more than one comma, keep only the first decimal part
        if (value.split(',').length > 2) {
            const parts = value.split(',');
            value = parts[0] + ',' + parts.slice(1).join(''); // Keep the first comma and join the rest without any additional commas
        }
    
        // If the value contains invalid characters, remove them
        if (!validCharacters.test(value)) {
            value = value.replace(/[^0-9,]/g, ''); // Remove everything that is not a number or a comma
        }
    
        // Update the row value and refresh the grid
        row.setValue(value.toString());
        features.instance.repaint();
        features.instance.focus(features.instance.getCellElement(row.rowIndex, 'value')!);
    }
    onFeaturesSaving(row: any) {
      let value = row.value;
    
      if (value.endsWith(',')) {
          value = value.slice(0, -1);
      }
    
      row.setValue(value);
    }

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }

}
